import { ExternalLink } from 'react-feather';
import { OutboundLink } from 'gatsby-plugin-google-gtag';
import { usePopper } from 'react-popper';
import { CryptoUtils } from 'utils/cryptoUtils';
import CopyWithAnimate from '../Copy/CopyWithAnimate';
import FormatFloat from '../FormatPercent/FormatFloat';
import FullHr from '../FullHr';
import Blockie from '../Blockie/Blockie';
import ConnectButton from '../ConnectButton/ConnectButton';
import { Transactions } from '../Transactions';
import { ChainUtils } from 'utils/chainUtils';
import { useConnectModal, useChainModal } from '@rainbow-me/rainbowkit';
import { useAccount, useBalance, useDisconnect } from 'wagmi';
import { CustomChain } from 'containers/WalletProvider/WalletProvider';
import React, { useState } from 'react';
import { chainLogoSrc } from 'constants/constants';

type Props = {
  isOnBottom?: boolean;
};

const WalletPopper: React.FC<Props> = (props) => {
  const { disconnect, disconnectAsync } = useDisconnect();
  const { openConnectModal } = useConnectModal();
  const { openChainModal } = useChainModal();
  const [changeModal, setChangeModal] = useState(false);

  const { address, connector, isConnected, chain } = useAccount();

  const nativeCurrencySymbol = chain?.nativeCurrency.symbol;

  const { data: balance } = useBalance({ address: address });

  const [referenceElement, setReferenceElement] = React.useState<unknown>(null);
  const [isActive, setIsActive] = React.useState(false);
  const [popperElement, setPopperElement] = React.useState<unknown>(null);
  const {
    styles,
    attributes,
    forceUpdate: popperUpdate,
  } = usePopper(referenceElement as Element, popperElement as HTMLElement, {
    placement: props.isOnBottom ? 'top' : 'bottom-end',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: props.isOnBottom ? [-10, 10] : [0, 8],
        },
      },
    ],
  });

  React.useEffect(() => {
    if (changeModal && openConnectModal) {
      openConnectModal();
      setChangeModal(false);
    }
  }, [changeModal, setChangeModal, openConnectModal]);

  const handleOnChangeNetworkClick = () => {
    void (async () => {
      await disconnectAsync();
      setChangeModal(true);
      setPopperIsActive(false);
    })();
  };

  React.useEffect(() => {
    // close popper when clicked out
    const handleOnClick = (ev) => {
      if (
        !(ev.composedPath() as Array<HTMLElement>).includes(
          popperElement as HTMLElement
        ) &&
        !(ev.composedPath() as Array<HTMLElement>).includes(
          referenceElement as HTMLElement
        )
      ) {
        setIsActive(false);
      }
    };
    document.addEventListener('click', handleOnClick);
    return () => document.removeEventListener('click', handleOnClick);
  });

  const setPopperIsActive = (isA: boolean) => {
    if (popperUpdate) {
      void popperUpdate();
    }
    setIsActive(isA);
  };

  const renderWalletControls = () => {
    if (isConnected && chain?.name) {
      return (
        <div
          className={`${
            isActive && 'active'
          } wallet-container nav-button-bg-color inline-flex items-center w-48 sm:w-64 h-10 ml-3 rounded-[10px] overflow-hidden`}
          id="web3-status-connected"
          onClick={() => setPopperIsActive(!isActive)}
          ref={setReferenceElement}
        >
          <div
            className="bg-neutral-200 w-24 rounded-l-[10px] inline-flex items-center h-10"
          >
            <img src={chainLogoSrc((chain as CustomChain)?.id)} alt={(chain as CustomChain)?.name} className="h-5 mx-1" />
            <FormatFloat value={balance ? balance.formatted : 0} trunc={false} />
          </div>
          <div className="inline-flex justify-between items-center ">
            <div className="flex flex-row justify-start items-center">
              <div className="hidden sm:block primary-text-color">
                <div className="font-medium mx-2 primary-text-color">
                  {ChainUtils.ellipseAddress(address, 4)}
                </div>
              </div>
              <div className="block sm:hidden">
                <div className="font-medium mx-2 primary-text-color">
                  {ChainUtils.ellipseAddress(address, 2)}
                </div>
              </div>
              <Blockie address={address as string} size={20} />
            </div>
            <img
              src="../../assets/image/chevron-down.svg"
              alt="expand"
              className="hidden sm:inline-block w-3 mx-3"
            />
          </div>
        </div>
      );
      // eslint-disable-next-line no-else-return
    } else if (isConnected && !chain) {
      return (
        <button
          onClick={openChainModal}
          type="button"
          className="wallet-button-bg-color primary-text-color cursor-pointer ml-3 rounded-[10px] h-10 flex items-center px-3"
        >
          Wrong network
        </button>
      );
    } else if (openConnectModal) {
      return (
        <ConnectButton
          onClick={openConnectModal}
          disabled={false}
          elRef={setReferenceElement}
        />
      );
    }
  };

  const handleOnDisconnectClick = () => {
    disconnect();
    setPopperIsActive(false);
  };

  const getModalContent = () => {
    if (address) {
      return (
        <div className="flex flex-col">
          <div className="flex flex-row justify-between sub-text wallet-name">
            Connected to {chain?.name} with {connector?.name}
          </div>
          <div className="flex flex-row justify-between mt-3">
            <div className="inline-flex items-center">
              <Blockie address={address} className="mr-5" size={20} />
              {CryptoUtils.shortenAddress(address)}
              <CopyWithAnimate value={address} />
            </div>
            <div className="flex flex-row flex-wrap justify-end">
              <button
                type="button"
                className="wallet-button-bg-color primary-color cursor-pointer ml-2 my-1 button-secondary"
                onClick={handleOnDisconnectClick}
                data-dd-actionon-name="Wallet Disconnect"
              >
                Disconnect
              </button>
              <button
                type="button"
                className="wallet-button-bg-color primary-color cursor-pointer ml-2 my-1 button-secondary"
                onClick={handleOnChangeNetworkClick}
                data-dd-action-name="Wallet Change Network"
              >
                Change
              </button>
            </div>
          </div>
          <OutboundLink
            target="_blank"
            href={chain?.blockExplorers?.default.url}
          >
            <div className="flex flex-row justify-start mt-3">
              <span className="sub-text">
                View on {chain?.blockExplorers?.default.name}
              </span>
              <ExternalLink width={16} height={16} className="ml-1" />
            </div>
          </OutboundLink>
        </div>
      );
    }
    return (
      <>
        <span>TBD</span>
      </>
    );
  };

  const modalClassName = `primary-text-color w-full sm:w-[460px] z-[5000] rounded-[10px] absolute right-full shadow-[0_2px_50px_0_rgba(0,0,0,0.25)] ${
    isActive ? 'visible' : 'invisible'
  }`;

  return (
    <>
      <div className="cursor-pointer">{renderWalletControls()}</div>
      <div
        className={modalClassName}
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}
        data-dd-action-name="Toggle Wallet Popper"
      >
        <div className="flex flex-col">
          {chain?.name && address && (
            <div className="p-4 w-full rounded-[10px] wallet-popper-inner-bg-color">
              {getModalContent()}
              {/* TODO: This needs to be done, we should read from the provider I assume to get the account transactions in flight */}
              <FullHr />
              <Transactions />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default WalletPopper;
